import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Sparkles, ArrowRight, Send, Loader2, Wand2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { products } from '../../data/products';
import { demoPages } from '../../data/demoPages';

const aiExamples = [
  { emoji: '✈️', text: "I'm going to Italy for 10 days." },
  { emoji: '👶', text: "I just had a baby." },
  { emoji: '💒', text: "I'm getting married next summer." },
  { emoji: '💰', text: "I want to save $10,000 this year." },
  { emoji: '🏋️', text: "I want to get in better shape." },
  { emoji: '📚', text: "I'm starting university." },
  { emoji: '🏡', text: "We're renovating our house." },
];

export const AIChatAssistant = ({ onPreviewProduct }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiSessionId, setAiSessionId] = useState(null);
  const [aiExampleIdx, setAiExampleIdx] = useState(0);
  const aiChatEndRef = useRef(null);

  useEffect(() => {
    const exTimer = setInterval(() => setAiExampleIdx(p => (p + 1) % 7), 3500);
    return () => clearInterval(exTimer);
  }, []);

  useEffect(() => {
    if (aiChatEndRef.current) aiChatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages, aiResult]);

  const handleAiSuggest = async (query) => {
    const q = query || aiQuery;
    if (!q.trim() || q.trim().length < 3) return;

    // Require auth for AI suggestions
    if (!isAuthenticated) {
      setAiMessages(prev => [...prev,
        { role: 'user', text: q },
        { role: 'assistant', text: 'Please sign in to use the AI assistant. It only takes a moment!' },
      ]);
      setAiQuery('');
      return;
    }

    const newMessages = [...aiMessages, { role: 'user', text: q }];
    setAiMessages(newMessages);
    setAiQuery('');
    setAiLoading(true);
    try {
      const body = { query: q };
      if (aiSessionId) body.session_id = aiSessionId;
      if (aiMessages.length > 0) body.conversation_history = newMessages.map(m => m.text);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('AI request failed');
      const data = await res.json();
      if (data.session_id) setAiSessionId(data.session_id);
      setAiResult(data);
      setAiMessages(prev => [...prev, { role: 'assistant', text: data.understanding }]);
    } catch {
      setAiMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiReset = () => { setAiResult(null); setAiMessages([]); setAiSessionId(null); setAiQuery(''); };

  return (
    <section data-testid="ai-assistant" className="py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        {aiMessages.length === 0 && (
          <>
            <div className="inline-flex items-center gap-2 bg-[#B49A88]/8 px-4 py-1.5 rounded-full mb-6">
              <Wand2 className="w-3.5 h-3.5 text-[#B49A88]" />
              <span className="text-[11px] font-sans font-bold text-[#B49A88] tracking-wider uppercase">AI Assistant</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-[#111111] tracking-tight leading-[1.1] mb-4">
              What do you need help<br className="hidden sm:block" /> with today?
            </h2>
            <p className="text-base font-sans text-[#6F6964] mb-8 max-w-xl mx-auto">
              Describe what you're planning or need — AI will find the perfect products and can create a completely new one, tailored just for you.
            </p>
          </>
        )}

        {aiMessages.length > 0 && (
          <div data-testid="ai-chat" className="max-w-2xl mx-auto mb-6 text-left">
            <div className="flex items-center justify-between mb-4">
              <div className="inline-flex items-center gap-2 bg-[#B49A88]/8 px-3 py-1 rounded-full">
                <Wand2 className="w-3 h-3 text-[#B49A88]" />
                <span className="text-[10px] font-sans font-bold text-[#B49A88] tracking-wider uppercase">Lumera AI</span>
              </div>
              <button data-testid="ai-reset-btn" onClick={handleAiReset} className="text-[11px] font-sans text-[#6F6964] hover:text-[#111111] transition-colors flex items-center gap-1">
                <X className="w-3 h-3" /> New search
              </button>
            </div>
            <div className="space-y-3 max-h-[200px] overflow-y-auto scrollbar-hide">
              {aiMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm font-sans ${msg.role === 'user' ? 'bg-stone-900 text-white rounded-br-md' : 'bg-primary/[0.05] text-stone-700 border border-primary/10 rounded-bl-md'}`}>
                    {msg.role === 'assistant' && <Sparkles className="w-3 h-3 text-primary inline mr-1.5 -mt-0.5" />}
                    {msg.text}
                  </div>
                </div>
              ))}
              {aiLoading && (
                <div className="flex justify-start">
                  <div className="bg-primary/[0.05] border border-primary/10 rounded-2xl rounded-bl-md px-4 py-2.5 flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-primary animate-pulse" />
                    <div className="flex gap-1">{[0,1,2].map(j => <div key={j} className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-bounce" style={{ animationDelay: `${j*0.15}s` }} />)}</div>
                  </div>
                </div>
              )}
              <div ref={aiChatEndRef} />
            </div>
          </div>
        )}

        <div data-testid="ai-input-area" className="relative max-w-2xl mx-auto">
          <div className="relative bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.06)] border border-stone-100/80 overflow-hidden transition-shadow duration-300 focus-within:shadow-[0_12px_50px_rgba(0,0,0,0.1)] focus-within:border-primary/20">
            <textarea data-testid="ai-input" value={aiQuery} onChange={e => setAiQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiSuggest(); } }}
              placeholder={aiMessages.length > 0 ? 'Tell me more... e.g. "Add a restaurant guide"' : aiExamples[aiExampleIdx].text}
              rows={aiMessages.length > 0 ? 1 : 2}
              className={`w-full px-6 pr-16 text-base font-sans text-stone-800 placeholder:text-stone-300 bg-transparent resize-none outline-none ${aiMessages.length > 0 ? 'py-3.5' : 'py-5'}`} />
            <button data-testid="ai-submit-btn" onClick={() => handleAiSuggest()} disabled={aiLoading || aiQuery.trim().length < 3}
              className={`absolute right-3 w-10 h-10 rounded-xl flex items-center justify-center bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 ${aiMessages.length > 0 ? 'bottom-1.5' : 'bottom-3'}`}>
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {aiMessages.length === 0 && (
          <div data-testid="ai-examples" className="flex flex-wrap justify-center gap-2 mt-6">
            {aiExamples.map((ex, i) => (
              <button key={i} data-testid={`ai-example-${i}`}
                onClick={() => { setAiQuery(ex.text); handleAiSuggest(ex.text); }}
                className="flex items-center gap-1.5 text-[12px] font-sans text-stone-500 bg-white border border-stone-100 rounded-full px-3.5 py-2 hover:bg-stone-50 hover:border-stone-200 hover:text-stone-700 transition-all duration-200 hover:shadow-sm">
                <span>{ex.emoji}</span> {ex.text}
              </button>
            ))}
          </div>
        )}

        {aiResult && !aiLoading && (
          <div data-testid="ai-results" className="mt-8 text-left max-w-2xl mx-auto animate-fade-in-up">
            {aiResult.recommended_products?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-serif font-bold text-stone-800 mb-4">We found these products for you</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {aiResult.recommended_products.map((rec, i) => {
                    const matchedProduct = products.find(p => p.title === rec.title);
                    if (!matchedProduct || !demoPages[rec.title]) return null;
                    return (
                      <button key={i} data-testid={`ai-rec-${i}`}
                        onClick={() => onPreviewProduct(matchedProduct)}
                        className="flex items-center gap-3 p-3 rounded-xl bg-white border border-stone-100 hover:border-primary/20 hover:shadow-md transition-all duration-200 text-left group">
                        <div className="w-14 flex-shrink-0 rounded-lg overflow-hidden shadow-sm border border-stone-50 aspect-[3/4]" style={{ width: '56px' }}>
                          {demoPages[rec.title]?.pages[0]({ accent: matchedProduct.colors.accent })}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-sans font-bold text-stone-800 group-hover:text-primary transition-colors">{rec.title}</p>
                          <p className="text-[11px] font-sans text-stone-400 mt-0.5 line-clamp-2">{rec.reason}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-primary flex-shrink-0 transition-colors" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {aiResult.bonus_suggestions?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-sans font-bold text-stone-500 uppercase tracking-wider mb-3">You might also need</h3>
                <div className="flex flex-wrap gap-2">
                  {aiResult.bonus_suggestions.map((s, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 text-[12px] font-sans text-stone-600 bg-stone-50 border border-stone-100 rounded-full px-3 py-1.5">
                      <Sparkles className="w-3 h-3 text-primary" /> {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {aiResult.custom_product && (
              <div data-testid="ai-custom-cta" className="p-5 rounded-2xl bg-gradient-to-br from-stone-900 to-stone-800 text-white">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0"><Wand2 className="w-5 h-5 text-primary" /></div>
                  <div className="flex-1">
                    <p className="text-xs font-sans font-bold text-primary uppercase tracking-wider mb-1">Custom-made for you</p>
                    <p className="text-base font-serif font-bold text-white mb-1">{aiResult.custom_product.title}</p>
                    <p className="text-sm font-sans text-stone-400 mb-4">{aiResult.custom_product.description}</p>
                    <Button data-testid="ai-generate-btn"
                      onClick={() => {
                        const cp = aiResult.custom_product;
                        navigate(isAuthenticated ? `/generator?type=${cp.type}&prefill_topic=${encodeURIComponent(cp.topic)}` : '/register');
                      }}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl px-6 py-5 font-sans font-bold text-sm shadow-lg hover:shadow-xl transition-all">
                      Lag mitt skreddersydde product <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <p className="text-center text-[11px] font-sans text-stone-400 mt-4">
              Type more in the field above to refine recommendations
            </p>
          </div>
        )}
      </div>
    </section>
  );
};
